import { forwardRef, useRef, useState } from 'react';
import classNames from 'classnames';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import CognitoCustomerModal from '@/components/commons/Customer/CognitoCustomerModal';
import { CustomerFormType } from '@/components/commons/Customer/types';
import i18n from '@/settings/i18n';
import AccountDrawer from './Drawer';
import AccountMenu from './Menu';
import AccountIcon from './AccountIcon';

const t = i18n.init('shared');

const AccountButton = forwardRef<
  HTMLButtonElement,
  {
    className?: string;
    label: string;
    initial?: string;
    onClick: () => void;
  }
>(({ className, label, initial, onClick }, ref) => (
  <button
    ref={ref}
    className={classNames(
      'flex h-7 w-7 items-center justify-center gap-2 rounded-full outline-none transition duration-100 ease-in-out hover:scale-105',
      className,
    )}
    aria-label={label}
    onClick={onClick}
  >
    <AccountIcon initial={initial} />
  </button>
));

AccountButton.displayName = 'AccountButton';

const Account = () => {
  const { customer } = useCustomerContext();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showAccountMobile, setShowAccountMobile] = useState(false);
  const [showAccountDesktop, setShowAccountDesktop] = useState(false);

  const desktopTriggerRef = useRef<HTMLButtonElement>(null);

  const customerInitial =
    customer?.firstName?.[0] ?? customer?.email?.[0] ?? '';

  return (
    <div className="relative">
      {/* Desktop: opens the account menu when logged in, otherwise login */}
      <AccountButton
        ref={desktopTriggerRef}
        className="hidden lg:flex"
        label={customer ? t`Account` : t`Login`}
        initial={customerInitial}
        onClick={() =>
          customer
            ? setShowAccountDesktop((prev) => !prev)
            : setShowLoginModal(true)
        }
      />
      {/* Mobile: opens the account drawer when logged in, otherwise login */}
      <AccountButton
        className="lg:hidden"
        label={customer ? t`Account` : t`Login`}
        initial={customerInitial}
        onClick={() =>
          customer ? setShowAccountMobile(true) : setShowLoginModal(true)
        }
      />

      <CognitoCustomerModal
        isOpen={showLoginModal}
        formType={CustomerFormType.LOGIN}
        setIsOpen={setShowLoginModal}
      />

      <AccountMenu
        open={showAccountDesktop}
        setOpen={setShowAccountDesktop}
        triggerRef={desktopTriggerRef}
      />
      <AccountDrawer open={showAccountMobile} setOpen={setShowAccountMobile} />
    </div>
  );
};

export default Account;
